/**
 * Client-side HTML sanitizer (defense in depth).
 *
 * The backend already sanitizes rendered-markdown HTML before returning it,
 * but anything passed to Svelte's {@html} should be sanitized at the sink too,
 * since {@html} performs no escaping. DOM-based allowlist: unknown tags are
 * unwrapped to their text, only safe attributes are kept, and javascript:/
 * data: URLs are dropped.
 *
 * @module lib/sanitizeHtml
 */

/** Tag set appropriate for rendered markdown (headings, lists, tables, code). */
const MARKDOWN_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li',
  'b', 'i', 'em', 'strong', 'del', 's', 'sub', 'sup',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'span', 'div',
]);

const ALLOWED_ATTRS = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title']),
  th: new Set(['align']),
  td: new Set(['align']),
  '*': new Set(['class']),
};

/**
 * Tag set for the rich-text fields authored in the product/variant editor.
 * Mirrors the backend's rich-text allowlist so the UI and the proposal PDF
 * agree on what a description may contain.
 */
const RICH_TEXT_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li',
  'b', 'i', 'em', 'strong', 'del', 's', 'u', 'sub', 'sup',
  'a',
]);

/** No `class` here: a description must not be able to borrow app styles. */
const RICH_TEXT_ATTRS = { a: new Set(['href']) };

const EMPTY = new Set();

/** Never allowed, and their text content is dropped with them. */
const NON_TEXT_TAGS = new Set([
  'script', 'style', 'noscript', 'template', 'textarea', 'title',
  'iframe', 'object', 'embed',
]);

const SAFE_URL = /^(https?:|mailto:)/i;

/**
 * Sanitize an HTML string for safe use with {@html}.
 *
 * @param {string} html Untrusted HTML
 * @param {Set<string>} [allowedTags] Tag allowlist (defaults to the markdown set)
 * @param {Record<string, Set<string>>} [allowedAttrs] Per-tag attribute allowlist
 * @returns {string} sanitized HTML
 */
export function sanitizeHtml(html, allowedTags = MARKDOWN_TAGS, allowedAttrs = ALLOWED_ATTRS) {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  /**
   * @param {Node} node
   * @returns {Node|null}
   */
  function clean(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(node.textContent);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tag = node.tagName.toLowerCase();

    // Tags whose content is code, not prose: drop the element entirely rather
    // than unwrapping it, so a <script> body does not surface as visible text.
    if (NON_TEXT_TAGS.has(tag)) {
      return null;
    }

    // Disallowed tag: unwrap to its (cleaned) children, dropping the tag.
    if (!allowedTags.has(tag)) {
      const fragment = document.createDocumentFragment();
      for (const child of node.childNodes) {
        const cleaned = clean(child);
        if (cleaned) fragment.appendChild(cleaned);
      }
      return fragment;
    }

    const el = document.createElement(tag);
    const allowed = allowedAttrs[tag] || EMPTY;
    const global = allowedAttrs['*'] || EMPTY;

    for (const attr of node.attributes) {
      const name = attr.name.toLowerCase();
      // Never copy event handlers or any unlisted attribute.
      if (!allowed.has(name) && !global.has(name)) continue;
      if ((name === 'href' || name === 'src') && !SAFE_URL.test(attr.value.trim())) continue;
      el.setAttribute(name, attr.value);
    }
    if (tag === 'a' && el.getAttribute('target') === '_blank') {
      el.setAttribute('rel', 'noopener noreferrer nofollow');
    }

    for (const child of node.childNodes) {
      const cleaned = clean(child);
      if (cleaned) el.appendChild(cleaned);
    }
    return el;
  }

  const out = document.createElement('div');
  for (const child of doc.body.childNodes) {
    const cleaned = clean(child);
    if (cleaned) out.appendChild(cleaned);
  }
  return out.innerHTML;
}

/**
 * Sanitize a product/variant description before rendering it with {@html}.
 * Stricter than {@link sanitizeHtml}: only editor markup, and no `class`
 * (legacy rows stored before the backend started sanitizing on write may still
 * carry one).
 *
 * @param {string} html Rich-text HTML as stored on the product or variant
 * @returns {string} sanitized HTML
 */
export function sanitizeRichText(html) {
  return sanitizeHtml(html, RICH_TEXT_TAGS, RICH_TEXT_ATTRS);
}
